<template>
  <div>
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="สถานะการศึกษา" class="h-full">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="psEduStatusChart"
              :series="eduStatus.series"
              :labels="eduStatus.labels"
              unit="คน"
              :height="200"
              :show-data-label="true"
              :palette="eduStatusPalette"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="สาเหตุในการออกจากการศึกษากลางคัน" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="psDropoutReasonChart"
              type="bar"
              :data="dropoutReasonData"
              :options="yellowBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-6 mt-6">
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ระดับชั้นที่กำลังศึกษา" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="psCurrentLevelChart"
              type="bar"
              :data="currentLevelData"
              :options="navyBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-6">
        <ChartCard title="ระดับชั้นการศึกษาสูงสุด (กรณีไม่ได้กำลังศึกษาแล้ว)" class="h-full">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="psHighestLevelChart"
              type="bar"
              :data="highestLevelData"
              :options="redBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChartCard from "../../container/ChartCard.vue";
import ChartWithLegend from "../../container/ChartWithLegend.vue";
import DashboardBarEChart from "../../container/DashboardBarEChart.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const props = defineProps({
  data: { type: Object, default: () => ({}) },
});

const P = { navy: "#3A506A", blue: "#20A0D7", yellow: "#F4D35E", green: "#11D67A", red: "#F73C4B" };

const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio,
    plugins: {
      legend: { display: legend, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } } },
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const numVal = horizontal ? ctx.parsed.x : ctx.parsed.y;
            return `${ctx.dataset.label}: ${globalNumDecFixed(numVal)} คน`;
          },
        },
      },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};

const toNumber = (value) => Number(value || 0);

const eduStatus = computed(() => {
  const items = props.data?.study_status || [];
  if (items.length) {
    return {
      labels: items.map((item) => item.name || ""),
      series: items.map((item) => toNumber(item.total)),
    };
  }
  return {
    labels: [],
    series: [],
  }
});
const EDU_STATUS_COLOR_BY_LABEL = { "ไม่เรียนแล้ว": "#3A506A", "กำลังเรียนอยู่": "#11D67A", "ไม่เคยเรียน": "#F73C4B" };
const eduStatusPalette = computed(() =>
  eduStatus.value.labels.map((label) => EDU_STATUS_COLOR_BY_LABEL[label] || "#606468")
);


const dropoutReasonData = computed(() => {
  const items = props.data?.dropout_causes || [];
  const dropoutDisplay = [
    { key: "d1", display: ["ไม่สามารถรับภาระค่าใช้", "จ่ายจากการศึกษาได้"] },
    { key: "d2", display: ["เพื่อไปประกอบอาชีพ หรือ", "ไปฝึกอบรมทักษะอาชีพ"] },
    { key: "d3", display: ["สมรสหรือมีบุตร"] },
    { key: "d4", display: ["ต้องช่วยงานบ้าน (อาทิ ดูแลผู้สูง", "อายุหรือเด็กเล็ก ถูบ้าน ซักผ้า)"] },
    { key: "d5", display: ["สิ่งที่เรียนไม่สอดคล้องกับความต้อง", "การเบื่อ ไม่อยากเรียน ไม่ชอบเรียน"] },
    { key: "d6", display: ["ครอบครัวไม่สนับสนุนให้เรียน", "ไม่เห็นความสำคัญของการศึกษา"] },
    { key: "d7", display: ["มีปัญหาด้านสุขภาพ/มีความพิการ"] },
    { key: "other", display: ["อื่นๆ"] }
  ]

  const dropoutMap = {
    "ไม่สามารถรับภาระค่าใช้จ่ายจากการศึกษาได้": "d1",
    "เพื่อไปประกอบอาชีพ หรือไปฝึกอบรมทักษะอาชีพ": "d2",
    "สมรสหรือมีบุตร": "d3",
    "ต้องช่วยงานบ้าน (อาทิ ดูแลผู้สูงอายุหรือเด็กเล็ก ถูบ้าน ซักผ้า)": "d4",
    "สิ่งที่เรียนไม่สอดคล้องกับความต้องการ เบื่อ ไม่อยากเรียน ไม่ชอบเรียน": "d5",
    "ครอบครัวไม่สนับสนุนให้เรียน ไม่เห็นความสำคัญของการศึกษา": "d6",
    "มีปัญหาด้านสุขภาพ/มีความพิการ": "d7",
    "อื่นๆ": "other",
    "อื่น ๆ": "other"
  };

  
  if (items.length) {
    return {
      labels: dropoutDisplay.map((item) => item.display),
      datasets: [
        bar("จำนวนคน", "#f69757", dropoutDisplay.map((d) =>
          toNumber(
            items.find(
              (item) => dropoutMap[(item.name || "")] === d.key
            )?.total
          )
        ),
        { barPercentage: 0.4 }
      )]
    };
  }
  return {
    labels: [],
    datasets: [],
  }
});
const eduLevelDisplay = [
  { key: "d1", display: ["ก่อนประถมศึกษา"] },
  { key: "d2", display: ["ประถมศึกษา"] },
  { key: "d3", display: ["มัธยมศึกษาตอนต้น"] },
  { key: "d4", display: ["มัธยมศึกษาตอนปลาย"] },
  { key: "d5", display: ["สูงกว่ามัธยมศึกษาตอนปลาย"] },
  { key: "d6", display: ["อาชีวศึกษา"] },
  { key: "d7", display: ["ประกาศนียบัตรระดับสูงกว่ามัธยมศึก", "ษาตอนปลายแต่ต่ำกว่าอนุปริญญา"] },
  { key: "d8", display: ["ปริญญาตรี"] },
  { key: "d9", display: ["ปริญญาโท"] },
  { key: "d10", display: ["ปริญญาเอก"] },
  { key: "d11", display: ["ดุริยางค์ทหาร"] },
  { key: "d12", display: ["การศึกษาทางศาสนา:", "พุทธศึกษา"] },
  { key: "d13", display: ["การศึกษาอื่นๆ: การศึกษา", "ที่เทียบระดับไม่ได้"] },
  { key: "d14", display: ["การศึกษาอื่นๆ: หลักสูตร", "ที่ไม่ได้วุฒิการศึกษา"] },
  { key: "d15", display: ["การศึกษาอื่นๆ: เรียนแต่", "ไม่ทราบระดับการศึกษา"] },
]
const eduLevelMap = {
  "ก่อนประถมศึกษา": "d1",
  "ประถมศึกษา": "d2",
  "มัธยมศึกษาตอนต้น": "d3",
  "มัธยมศึกษาตอนปลาย": "d4",
  "สูงกว่ามัธยมศึกษาตอนปลาย": "d5",
  "อาชีวศึกษา": "d6",
  "ประกาศนียบัตรระดับสูงกว่ามัธยมศึกษาตอนปลายแต่ต่ำกว่าอนุปริญญา": "d7",
  "ปริญญาตรี": "d8",
  "ปริญญาโท": "d9",
  "ปริญญาเอก": "d10",
  "ดุริยางค์ทหาร": "d11",
  "การศึกษาทางศาสนา: พุทธศึกษา": "d12",
  "การศึกษาอื่นๆ: การศึกษาที่เทียบระดับไม่ได้": "d13",
  "การศึกษาอื่นๆ: หลักสูตรที่ไม่ได้วุฒิการศึกษา": "d14",
  "การศึกษาอื่นๆ: เรียนแต่ไม่ทราบระดับการศึกษา": "d15"
}

const currentLevelData = computed(() => {
  const items = props.data?.current_edu_level || [];
  if (items.length) {
    return {
      labels: eduLevelDisplay.map((item) => item.display),
      datasets: [
        bar(
          "จำนวนคน",
          "#4ca37a", 
          eduLevelDisplay.map((d) =>
            toNumber(
              items.find(
                (item) => eduLevelMap[(item.name || "")] === d.key
              )?.total
            )
          ),
          { barPercentage: 0.4 }
        )
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  };
});

const highestLevelData = computed(() => {
  const items = props.data?.highest_edu_level || [];
  if (items.length) {
    return {
      labels: eduLevelDisplay.map((item) => item.display),
      datasets: [
        bar(
          "จำนวนคน",
          "#009688",
          eduLevelDisplay.map((d) =>
            toNumber(
              items.find(
                (item) => eduLevelMap[(item.name || "")] === d.key
              )?.total
            )
          ),
          { barPercentage: 0.4 }
        )
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  };
});

const yellowBarOptions = barOptions({ aspectRatio: 1.8 });
const navyBarOptions = barOptions({ aspectRatio: 2 });
const redBarOptions = barOptions({ aspectRatio: 2 });
</script>
